import Vue from 'vue'
import session from '@/shared/session'

// initial state
const initialState = () => ({
	patient: {
		history: null,
		allergies: [],
		bloodtype: null,
		vitals: [],
		measurements: [],
		attests: [],
		lifestyle: [],
		ontslagbrief: [],
		cbbformulier: []
	}
})

const state = initialState()

// getters
const getters = {
	getPatientHistory: (state) => () => {
		return state.patient.history
	},
	getPatientBloodType: (state) => () => {
		return state.patient.bloodtype
	},
	getPatientVitals: (state) => () => {
		return state.patient.vitals
	},
	getPatientMeasurements: (state) => () => {
		return state.patient.measurements
	},
	getPatientAttests: (state) => () => {
		return state.patient.attests
	},
	getPatientAllergies: (state) => () => {
		return state.patient.allergies
	},
	getPatientLifestyle: (state) => () => {
		return state.patient.lifestyle
	},
	getPatientOntslagbrief: (state) => () => {
		return state.patient.ontslagbrief
	},
	getPatientCbbFormulier: (state) => () => {
		return state.patient.cbbformulier
	}
}

// actions
const actions = {
	resetState ({ commit }) {
		commit('reset_state')
	},

	async filterAttestsBatch({ state, commit }, data) {
		const attests = await session
			.get("/api/health/attests/batch/", {
				params: data,
			})
		return attests.data
	},

	async exportBatch({ commit }, data) {
		const batch = await session
			.post(`/api/health/attests/export_batch/`, data)

		return batch.data
	},

	async getCurrentExports({ commit }, data) {
		const batch = await session
			.get(`/api/health/attests/exports_batch/`, {
				params: data
			})

		return batch.data
	},

	async getRunningExports({ commit }, data) {
		const batch = await session
			.get(`/api/health/attests/running_exports_batch/`, {
				params: data
			})

		return batch.data ? batch.data.running : false
	},

	async getCurrentExport({ commit }, data) {
		const batch = await session
			.get(`/api/health/attests/current_export_batch/`, {
				params: data
			})

		return batch.data
	},

	async getHistoryForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "history", { root: true })

		const history  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/history/`)

		commit("set_health", {key: 'history', value: history.data})

		dispatch("loading/end", "history", { root: true })

		return state.patient.history
	},

	async getAllergiesForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "allergies", { root: true })

		const allergies  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/allergies/`)

		commit("set_health", {key: 'allergies', value: allergies.data})

		dispatch("loading/end", "allergies", { root: true })

		return state.patient.allergies
	},

	async getBloodTypeForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "bloodtype", { root: true })

		const bloodtype  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/bloodtype/`)

		commit("set_health", {key: 'bloodtype', value: bloodtype.data})

		dispatch("loading/end", "bloodtype", { root: true })

		return state.patient.bloodtype
	},

	async getVitalsForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "vitals", { root: true })

		const vitals  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/vitals/`)

		commit("set_health", {key: 'vitals', value: vitals.data})

		dispatch("loading/end", "vitals", { root: true })

		return state.patient.vitals
	},

	async getVitalsChartDataForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "vitals_chart", { root: true })

		const chart  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/vitals_chart/`)

		dispatch("loading/end", "vitals_chart", { root: true })

		return chart.data
	},

	async getMeasurementsForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "measurements", { root: true })

		const measurements  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/measurements/`)

		commit("set_health", {key: 'measurements', value: measurements.data})

		dispatch("loading/end", "measurements", { root: true })

		return state.patient.measurements
	},

	async getMeasurmentsChartDataForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "measurements_chart", { root: true })

		const chart  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/measurements_chart/`)

		dispatch("loading/end", "measurements_chart", { root: true })

		return chart.data
	},

	async getAttestsForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "attests", { root: true })

		const attests  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/attests/`)

		commit("set_health", {key: 'attests', value: attests.data})

		dispatch("loading/end", "attests", { root: true })

		return state.patient.attests
	},

	createPatientHistory({ commit, dispatch, rootState }, data) {
		dispatch("loading/load", "history", { root: true })
		return session
				.post(`/api/health/history/`, {...data, patient: rootState.patients.current?.id})
				.then(() => {
					return dispatch("getHistoryForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "history", { root: true })
	      })
	},

	updatePatientHistory({ commit, dispatch }, data) {
		dispatch("loading/load", "history", { root: true })
		return session
				.put(`/api/health/history/${data.id}/`, data.history)
				.then(() => {
					return dispatch("getHistoryForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "history", { root: true })
	      })
	},

	createPatientBloodType({ commit, dispatch, rootState }, data) {
		dispatch("loading/load", "bloodtype", { root: true })
		return session
				.post(`/api/health/bloodtype/`, {...data, patient: rootState.patients.current?.id})
				.then(() => {
					return dispatch("getBloodTypeForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "bloodtype", { root: true })
	      })
	},

	updatePatientBloodType({ commit, dispatch }, data) {
		dispatch("loading/load", "bloodtype", { root: true })
		return session
				.put(`/api/health/bloodtype/${data.id}/`, data.bloodtype)
				.then(() => {
					return dispatch("getBloodTypeForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "bloodtype", { root: true })
	      })
	},

	createPatientAllergy({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "allergies", { root: true })
		return session
				.post(`/api/health/allergies/`, {...data, patient: rootState.patients.current?.id})
				.then(() => {
					return dispatch("getAllergiesForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "allergies", { root: true })
	      })
	},

	deletePatientAllergy({ dispatch, commit }, id) {
		dispatch("loading/load", "allergies", { root: true })
		return session
				.delete(`/api/health/allergies/${id}/`)
				.then(() => {
					return dispatch("getAllergiesForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "allergies", { root: true })
      })
	},

	async checkForAllergy({ commit, state, rootState }, data) {
			const has_allergy  = await session
				.get(`/api/patients/${rootState.patients.current?.id}/allergy_check/`, {
					params: data
				})

			return has_allergy.data
	},

	createPatientVitalsForCurrentConsult({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "vitals", { root: true })
		return session
				.post(`/api/health/vitals/`, {...data, decursus: rootState.patients.current?.current_consult?.decursus_id})
				.then(() => {
					return dispatch("getVitalsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "vitals", { root: true })
	      })
	},

	createPatientAdminVitals({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "vitals", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/vitals/`, data)
				.then(() => {
					return dispatch("getVitalsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "vitals", { root: true })
	      })
	},

	createPatientVitals({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "vitals", { root: true })
		return session
				.post(`/api/health/vitals/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getVitalsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "vitals", { root: true })
	      })
	},

	deletePatientVitals({ dispatch, commit }, id) {
		dispatch("loading/load", "vitals", { root: true })
		return session
				.delete(`/api/health/vitals/${id}/`)
				.then(() => {
					return dispatch("getVitalsForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "vitals", { root: true })
      })
	},

	createPatientMeasurementsForCurrentConsult({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "measurements", { root: true })
		return session
				.post(`/api/health/measurements/`, {...data, decursus: rootState.patients.current.current_consult?.decursus_id})
				.then(() => {
					return dispatch("getMeasurementsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "measurements", { root: true })
	      })
	},

	createPatientAdminMeasurements({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "measurements", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/measurements/`, data)
				.then(() => {
					return dispatch("getMeasurementsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "measurements", { root: true })
	      })
	},

	createPatientMeasurements({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "measurements", { root: true })
		return session
				.post(`/api/health/measurements/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getMeasurementsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "measurements", { root: true })
	      })
	},

	deletePatientMeasurements({ dispatch, commit }, id) {
		dispatch("loading/load", "measurements", { root: true })
		return session
				.delete(`/api/health/measurements/${id}/`)
				.then(() => {
					return dispatch("getMeasurementsForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "measurements", { root: true })
      })
	},

	createPatientAttestForCurrentConsult({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "attests", { root: true })
		return session
				.post(`/api/health/attests/`, {...data, decursus: rootState.patients.current?.current_consult?.decursus_id})
				.then(() => {
					return dispatch("getAttestsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "attests", { root: true })
	      })
	},

	createPatientAdminAttest({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "attests", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/attests/`, data)
				.then(() => {
					return dispatch("getAttestsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "attests", { root: true })
	      })
	},

	createPatientAttest({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "attests", { root: true })
		return session
				.post(`/api/health/attests/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getAttestsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "attests", { root: true })
	      })
	},

	deletePatientAttest({ dispatch, commit }, id) {
		dispatch("loading/load", "attests", { root: true })
		return session
				.delete(`/api/health/attests/${id}/`)
				.then(() => {
					return dispatch("getAttestsForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "attests", { root: true })
      })
	},

	async getLifestyleForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "lifestyle", { root: true })

		const lifestyle  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/lifestyle/`)

		commit("set_health", {key: 'lifestyle', value: lifestyle.data})

		dispatch("loading/end", "lifestyle", { root: true })

		return state.patient.lifestyle
	},

	createPatientAdminLifestyle({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "lifestyle", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/lifestyle/`, data)
				.then(() => {
					return dispatch("getLifestyleForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "lifestyle", { root: true })
	      })
	},

	createPatientLifestyle({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "lifestyle", { root: true })
		return session
				.post(`/api/health/lifestyle/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getLifestyleForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "lifestyle", { root: true })
	      })
	},

	deletePatientLifestyle({ dispatch, commit }, id) {
		dispatch("loading/load", "lifestyle", { root: true })
		return session
				.delete(`/api/health/lifestyle/${id}/`)
				.then(() => {
					return dispatch("getLifestyleForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "lifestyle", { root: true })
      })
	},

	async getOntslagbriefForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "ontslagbrief", { root: true })

		const ontslagbrief  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/ontslagbrief/`)

		commit("set_health", {key: 'ontslagbrief', value: ontslagbrief.data})

		dispatch("loading/end", "ontslagbrief", { root: true })

		return state.patient.ontslagbrief
	},

	createPatientAdminOntslagbrief({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "ontslagbrief", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/ontslagbrief/`, data)
				.then(() => {
					return dispatch("getOntslagbriefForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "ontslagbrief", { root: true })
	      })
	},

	createPatientOntslagbrief({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "ontslagbrief", { root: true })
		return session
				.post(`/api/health/ontslagbrief/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getOntslagbriefForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "ontslagbrief", { root: true })
	      })
	},

	deletePatientOntslagbrief({ dispatch, commit }, id) {
		dispatch("loading/load", "ontslagbrief", { root: true })
		return session
				.delete(`/api/health/ontslagbrief/${id}/`)
				.then(() => {
					return dispatch("getOntslagbriefForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "ontslagbrief", { root: true })
      })
	},

	async getCbbFormulierForCurrentPatient({ commit, state, rootState, dispatch }) {
		if (!rootState.patients.current) {
			return
		}

		dispatch("loading/load", "cbbformulier", { root: true })

		const cbbformulier  = await session
			.get(`/api/patients/${rootState.patients.current?.id}/cbbformulier/`)

		commit("set_health", {key: 'cbbformulier', value: cbbformulier.data})

		dispatch("loading/end", "cbbformulier", { root: true })

		return state.patient.cbbformulier
	},

	createPatientAdminCbbFormulier({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "cbbformulier", { root: true })
		return session
				.post(`/api/patients/administration/${rootState.patients.current?.id}/cbbformulier/`, data)
				.then(() => {
					return dispatch("getCbbFormulierForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "cbbformulier", { root: true })
	      })
	},

	createPatientCbbFormulier({ dispatch, commit, rootState }, data) {
		dispatch("loading/load", "cbbformulier", { root: true })
		return session
				.post(`/api/health/cbbformulier/`, {...data, decursus: rootState.decursus.current?.id})
				.then(() => {
					return dispatch("getCbbFormulierForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "cbbformulier", { root: true })
	      })
	},

	deletePatientCbbFormulier({ dispatch, commit }, id) {
		dispatch("loading/load", "cbbformulier", { root: true })
		return session
				.delete(`/api/health/cbbformulier/${id}/`)
				.then(() => {
					return dispatch("getCbbFormulierForCurrentPatient")
				})
      .finally(() => {
        dispatch("loading/end", "cbbformulier", { root: true })
      })
	},

	attesToHTMLById({ commit, state, rootState }, id) {
		return session
				.get(`/api/health/attests/${id}/to_html/`)
				.then(letter => {
					return letter.data
				})
	},

	ontslagbriefToHTMLById({ commit, state, rootState }, id) {
		return session
				.get(`/api/health/ontslagbrief/${id}/to_html/`)
				.then(letter => {
					return letter.data
				})
	},

	cbbformulierToHTMLById({ commit, state, rootState }, id) {
		return session
				.get(`/api/health/cbbformulier/${id}/to_html/`)
				.then(letter => {
					return letter.data
				})
	},
}

// mutations
const mutations = {
  reset_state(state) {
		Object.assign(state, initialState())
  },

	set_health(state, {key, value}) {
		Vue.set(state.patient, key, value)
	},
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}